/**
 * Données SEO des articles de blog – requêtes GSC + intention commerciale.
 * Chaque article cible une requête avec titre, description, FAQ optionnelle.
 */

import { BLOG_POSTS_AR } from "./blog-posts-seo-ar";

export type BlogLocale = "fr" | "en" | "ar";

export interface BlogPostSEOItem {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  date: string;
  category: string;
  /** Pour FAQ schema et section FAQ en bas d'article */
  faq?: { question: string; answer: string }[];
  /** Optional EN; when present, used when locale is "en". */
  titleEn?: string;
  excerptEn?: string;
  categoryEn?: string;
  dateEn?: string;
  faqEn?: { question: string; answer: string }[];
  /** Optional full article body for BlogPost page rendering. */
  contentHtml?: string;
  /** Optional EN full article body for BlogPost page rendering. */
  contentHtmlEn?: string;
}

const buildBlogArticleTemplate = (data: {
  intro: string;
  sections: Array<{ heading: string; body: string }>;
  cityLinks: string[];
  listingLinks: string[];
  bookingCtaLabel: string;
  bookingCtaPath: string;
  contactCtaLabel: string;
  contactCtaPath: string;
  faqHint?: string;
}): string => {
  const sectionsHtml = data.sections
    .map(
      (section) =>
        `<h2>${section.heading}</h2><p>${section.body}</p>`
    )
    .join("");

  const cityLinksHtml = data.cityLinks.map((link) => `<li>${link}</li>`).join("");
  const listingLinksHtml = data.listingLinks.map((link) => `<li>${link}</li>`).join("");

  return `
    <p>${data.intro}</p>
    ${sectionsHtml}
    <h2>Liens utiles par ville</h2>
    <ul>${cityLinksHtml}</ul>
    <h2>Liens listing et offres</h2>
    <ul>${listingLinksHtml}</ul>
    <h2>Prêt à réserver ?</h2>
    <p>
      <a href="${data.bookingCtaPath}">${data.bookingCtaLabel}</a> ·
      <a href="${data.contactCtaPath}">${data.contactCtaLabel}</a>
    </p>
    ${data.faqHint ? `<p>${data.faqHint}</p>` : ""}
  `;
};

export const BLOG_POSTS_SEO: BlogPostSEOItem[] = [
  { id: "1", slug: "conseils-location-reussie", title: "Les meilleurs conseils pour une location réussie", excerpt: "Découvrez nos astuces pour tirer le meilleur parti de votre location de véhicule et éviter les pièges courants.", date: "15 Janvier 2024", category: "Conseils", titleEn: "Best tips for a successful rental", excerptEn: "Discover our tips to get the most out of your vehicle rental and avoid common pitfalls.", dateEn: "15 January 2024", categoryEn: "Tips" },
  { id: "2", slug: "choisir-vehicule-selon-besoins", title: "Comment choisir le bon véhicule selon vos besoins", excerpt: "Guide complet pour sélectionner le véhicule idéal en fonction de votre destination, du nombre de passagers et de vos préférences.", date: "10 Janvier 2024", category: "Guide", titleEn: "How to choose the right vehicle for your needs", excerptEn: "Complete guide to selecting the ideal vehicle based on your destination, number of passengers and preferences.", dateEn: "10 January 2024", categoryEn: "Guide" },
  { id: "3", slug: "assurance-protection-location", title: "Assurance et protection : tout comprendre", excerpt: "Explications détaillées sur les garanties incluses dans nos locations et comment vous protéger efficacement.", date: "5 Janvier 2024", category: "Assurance", titleEn: "Insurance and protection: everything you need to know", excerptEn: "Detailed explanations of the guarantees included in our rentals and how to protect yourself effectively.", dateEn: "5 January 2024", categoryEn: "Insurance" },
  { id: "4", slug: "destinations-maroc-voiture", title: "Top 10 des destinations à découvrir au Maroc en voiture", excerpt: "Explorez les plus beaux circuits routiers du Maroc et découvrez des destinations incontournables à visiter en location.", date: "1 Janvier 2024", category: "Voyage", titleEn: "Top 10 destinations to discover in Morocco by car", excerptEn: "Explore Morocco's most beautiful road trips and discover must-see destinations to visit by rental car.", dateEn: "1 January 2024", categoryEn: "Travel" },
  { id: "5", slug: "prix-location-voiture-maroc", title: "Quel est le prix d'une location voiture au Maroc ?", excerpt: "Tarifs moyens, facteurs de coût et conseils pour trouver une location au meilleur prix au Maroc.", date: "20 Février 2024", category: "Guide", faq: [{ question: "Quel est le prix moyen d'une location de voiture au Maroc ?", answer: "Les tarifs varient selon la ville, la saison et la catégorie. Sur RAKB, chaque annonce affiche le tarif journalier réel pour vos dates : comparez les citadines et compactes à Casablanca, Rabat, Marrakech ou Tanger sans supposer un prix plancher unique." }, { question: "Quels facteurs font varier le prix ?", answer: "La saison, la durée, la catégorie de véhicule, les options (livraison aéroport, franchise réduite) et la ville influencent le prix." }], titleEn: "What is the price of car rental in Morocco?", excerptEn: "Average rates, cost factors and tips to find the best-priced rental in Morocco.", dateEn: "20 February 2024", categoryEn: "Guide", faqEn: [{ question: "What is the average price of car rental in Morocco?", answer: "Rates vary by city, season, and category. On RAKB, each listing shows the real daily rate for your dates—compare small cars in Casablanca, Rabat, Marrakech or Tangier without assuming one nationwide floor price." }, { question: "What factors affect the price?", answer: "Season, duration, vehicle category, options (airport delivery, reduced excess) and city influence the price." }] },
  { id: "6", slug: "location-voiture-150-dh-possible", title: "Location voiture Maroc 150 DH : est-ce possible ?", excerpt: "Beaucoup de recherches ciblent « 150 DH » : voici comment interpréter ce budget et réserver au bon tarif sur RAKB.", date: "22 Février 2024", category: "Conseils", faq: [{ question: "La location à 150 DH/jour est-elle fiable ?", answer: "Sur RAKB, les agences partenaires sont vérifiées. Certaines citadines ou compactes peuvent s’afficher autour de ce niveau selon la période et la disponibilité, mais le montant exact figure toujours sur l’annonce pour vos dates — ce n’est pas une garantie pour toutes les réservations." }], titleEn: "Morocco car rental 150 DH: is it possible?", excerptEn: "Many searches target “150 DH”: here is how to read that budget and book at the right rate on RAKB.", dateEn: "22 February 2024", categoryEn: "Tips", faqEn: [{ question: "Is 150 DH/day rental reliable?", answer: "On RAKB, partner agencies are verified. Some small cars may appear near that level depending on timing and availability, but the exact amount is always on the listing for your dates—it is not guaranteed for every booking." }] },
  { id: "7", slug: "location-voiture-aeroport-casablanca-guide", title: "Location voiture aéroport Casablanca : guide complet", excerpt: "Tout savoir pour récupérer votre voiture à l'aéroport Mohammed V : réservation, tarifs, formalités.", date: "25 Février 2024", category: "Guide", faq: [{ question: "Où récupérer ma voiture à l'aéroport Casablanca ?", answer: "Les agences partenaires RAKB proposent la remise du véhicule directement à l'aéroport Mohammed V (CMN). Indiquez votre vol lors de la réservation." }] },
  { id: "8", slug: "caution-louer-voiture-maroc", title: "Faut-il une caution pour louer une voiture au Maroc ?", excerpt: "Comment fonctionne la caution en location au Maroc, options pour la réduire et conseils pratiques.", date: "28 Février 2024", category: "Conseils", faq: [{ question: "Quel montant de caution pour une location au Maroc ?", answer: "Le montant varie selon l'agence et le véhicule, souvent entre 2 000 et 5 000 DH. Il est bloqué sur votre carte et restitué à la fin si le véhicule est rendu sans dommage." }] },
  { id: "9", slug: "location-voiture-touristes-maroc", title: "Location voiture pour touristes au Maroc", excerpt: "Guide pour les voyageurs internationaux : documents, assurance, conseils et bonnes adresses.", date: "2 Mars 2024", category: "Voyage" },
  { id: "10", slug: "location-longue-duree-maroc", title: "Location voiture longue durée au Maroc : semaine, mois", excerpt: "Tarifs dégressifs, formalités et conseils pour une location de plusieurs semaines ou mois.", date: "5 Mars 2024", category: "Guide" },
  { id: "11", slug: "documents-louer-voiture-maroc", title: "Quels documents pour louer une voiture au Maroc ?", excerpt: "Permis, pièce d'identité, carte bancaire : la liste complète des documents requis.", date: "8 Mars 2024", category: "Conseils" },
  { id: "12", slug: "location-voiture-pas-cher-casablanca", title: "Comment trouver une location voiture pas chère à Casablanca ?", excerpt: "Astuces et comparateurs pour dénicher les meilleurs tarifs à Casablanca.", date: "10 Mars 2024", category: "Conseils" },
  { id: "13", slug: "location-aeroport-marrakech-menara", title: "Location voiture aéroport Marrakech Menara : guide", excerpt: "Récupérer votre voiture à l'aéroport Menara : réservation, tarifs, conseils.", date: "12 Mars 2024", category: "Guide" },
  { id: "14", slug: "age-minimum-location-voiture-maroc", title: "Quel âge minimum pour louer une voiture au Maroc ?", excerpt: "Règles d'âge et d'ancienneté du permis selon les agences et catégories.", date: "15 Mars 2024", category: "Conseils" },
  { id: "15", slug: "annulation-reservation-location-maroc", title: "Annulation et modification d'une réservation de location au Maroc", excerpt: "Conditions d'annulation, frais et flexibilité selon les offres RAKB.", date: "18 Mars 2024", category: "Conseils" },
  { id: "16", slug: "location-voiture-rabat-pas-cher", title: "Location voiture Rabat pas cher : nos conseils", excerpt: "Où et comment comparer les annonces à Rabat et réserver au tarif affiché pour vos dates.", date: "20 Mars 2024", category: "Guide" },
  { id: "17", slug: "location-voiture-marrakech-touristes", title: "Location voiture Marrakech pour touristes", excerpt: "Guide pratique pour les visiteurs : aéroport Menara, Médina, excursions.", date: "22 Mars 2024", category: "Voyage" },
  { id: "18", slug: "location-voiture-tanger-ferry", title: "Location voiture Tanger : aéroport et port Tanger Med", excerpt: "Récupérer votre voiture à l'aéroport Ibn Battouta ou après une traversée en ferry.", date: "25 Mars 2024", category: "Guide" },
  { id: "19", slug: "franchise-reduite-location-maroc", title: "Franchise réduite et options assurance en location au Maroc", excerpt: "Comprendre la franchise, la réduire et choisir les bonnes options.", date: "28 Mars 2024", category: "Assurance" },
  { id: "20", slug: "comparateur-location-voiture-maroc", title: "Pourquoi utiliser un comparateur de location voiture au Maroc ?", excerpt: "Avantages de comparer les offres (prix, avis, conditions) avant de réserver.", date: "1 Avril 2024", category: "Conseils" },
  {
    id: "21",
    slug: "location-voiture-casablanca-aeroport-prix-reel",
    title: "Location voiture Casablanca aéroport : prix réel, caution et réservation rapide",
    excerpt: "Guide high-intent pour réserver à l'aéroport Mohammed V avec prix réalistes, options de caution et conseils pour éviter les frais cachés.",
    date: "15 Avril 2024",
    category: "Guide",
    faq: [
      {
        question: "Quel prix réel pour une location voiture à l'aéroport Casablanca ?",
        answer: "Les fourchettes varient fortement selon la saison et l’annonce. Sur RAKB, filtrez par dates : les citadines affichent souvent des niveaux accessibles, tandis que les SUV restent en général plus chers — le tarif exact est sur chaque fiche avant réservation."
      },
      {
        question: "Faut-il une caution pour récupérer la voiture à Mohammed V ?",
        answer: "Oui, la plupart des agences demandent une caution, en général entre 2 000 et 5 000 DH selon catégorie et conditions d'assurance."
      }
    ],
    contentHtml: buildBlogArticleTemplate({
      intro:
        "Si vous atterrissez à Casablanca et cherchez une voiture immédiatement, ce guide répond à l'intention la plus fréquente: comparer vite, réserver sans stress et éviter les frais imprévus.",
      sections: [
        {
          heading: "Prix constatés à l'aéroport Mohammed V",
          body: "Le tarif dépend de la saison, de la durée et de la catégorie. Pour un court séjour, comparez d'abord les compactes et berlines économiques, puis vérifiez les conditions de kilométrage et de carburant avant de confirmer."
        },
        {
          heading: "Caution, assurance et documents",
          body: "Préparez permis valide, pièce d'identité et carte bancaire. Vérifiez la franchise, les exclusions et la méthode de blocage de caution. Une offre légèrement plus chère peut être plus avantageuse si la franchise est réduite."
        },
        {
          heading: "Processus de réservation recommandé",
          body: "Filtrez par ville, dates et catégorie. Comparez les conditions, pas seulement le prix. Choisissez une agence vérifiée, puis confirmez votre vol pour organiser la remise du véhicule au terminal."
        }
      ],
      cityLinks: [
        `<a href="/location-voiture/casablanca">Location voiture Casablanca</a>`,
        `<a href="/location-voiture/rabat">Location voiture Rabat</a>`,
        `<a href="/location-voiture/marrakech">Location voiture Marrakech</a>`,
        `<a href="/location-voiture/tanger">Location voiture Tanger</a>`
      ],
      listingLinks: [
        `<a href="/search?city=Casablanca">Voir toutes les voitures à Casablanca</a>`,
        `<a href="/search?city=Casablanca&category=compacte">Voir les compactes à Casablanca</a>`,
        `<a href="/search?city=Casablanca&category=suv">Voir les SUV à Casablanca</a>`
      ],
      bookingCtaLabel: "Réserver un véhicule à Casablanca",
      bookingCtaPath: "/search?city=Casablanca",
      contactCtaLabel: "Contacter l'équipe RAKB",
      contactCtaPath: "/contact",
      faqHint: "Consultez aussi la FAQ ci-dessous pour les questions sur la caution, l'assurance et la disponibilité."
    })
  },
  {
    id: "22",
    slug: "location-longue-duree-casablanca-rabat-marrakech",
    title: "Location longue durée au Maroc: Casablanca, Rabat, Marrakech (offres mensuelles)",
    excerpt: "Comparatif pratique des offres longue durée, coût mensuel, types de véhicules et critères pour choisir une location rentable.",
    date: "30 Avril 2024",
    category: "Guide",
    faq: [
      {
        question: "Quand la location longue durée devient-elle rentable ?",
        answer: "En général dès 14 à 30 jours, selon les remises appliquées par l'agence et la catégorie du véhicule."
      },
      {
        question: "Quelle ville offre le plus d'options longue durée ?",
        answer: "Casablanca a souvent l'offre la plus large, suivie de Rabat et Marrakech sur les catégories compacte, berline et SUV."
      }
    ],
    contentHtml: buildBlogArticleTemplate({
      intro:
        "La demande de location mensuelle augmente chez les professionnels, expatriés et voyageurs longue durée. Cet article vous aide à comparer les offres dans les trois villes les plus demandées.",
      sections: [
        {
          heading: "Comment lire un tarif longue durée",
          body: "Ne comparez pas uniquement le prix/jour. Vérifiez le plafond kilométrique, l'entretien inclus, la politique d'annulation et le montant de caution. Le coût total mensuel doit rester clair dès le départ."
        },
        {
          heading: "Choisir le bon véhicule selon usage",
          body: "Pour la ville quotidienne: compacte économique. Pour déplacements pro et confort: berline. Pour trajets mixtes et famille: SUV. Un bon match usage-véhicule réduit le coût carburant et les risques de surcoût."
        },
        {
          heading: "Casablanca vs Rabat vs Marrakech",
          body: "Casablanca: plus de disponibilité et de catégories. Rabat: bon équilibre budget/confort. Marrakech: forte demande saisonnière, donc réservation anticipée recommandée pour garantir les meilleures conditions."
        }
      ],
      cityLinks: [
        `<a href="/location-voiture/casablanca">Location voiture Casablanca</a>`,
        `<a href="/location-voiture/rabat">Location voiture Rabat</a>`,
        `<a href="/location-voiture/marrakech">Location voiture Marrakech</a>`
      ],
      listingLinks: [
        `<a href="/search?city=Casablanca">Voir le listing Casablanca</a>`,
        `<a href="/search?city=Rabat">Voir le listing Rabat</a>`,
        `<a href="/search?city=Marrakech">Voir le listing Marrakech</a>`
      ],
      bookingCtaLabel: "Comparer les offres longue durée",
      bookingCtaPath: "/search",
      contactCtaLabel: "Parler à un conseiller location",
      contactCtaPath: "/contact",
      faqHint: "La FAQ en bas de page couvre la rentabilité, la durée et la disponibilité par ville."
    })
  },
];

export const getBlogPostById = (id: string): BlogPostSEOItem | undefined =>
  BLOG_POSTS_SEO.find((p) => p.id === id);

export const getBlogPostBySlug = (slug: string): BlogPostSEOItem | undefined =>
  BLOG_POSTS_SEO.find((p) => p.slug === slug);

/** Returns localized title, excerpt, category, date, faq for a post. */
export const getBlogPostContentLocalized = (
  post: BlogPostSEOItem,
  locale: BlogLocale
): {
  title: string;
  excerpt: string;
  category: string;
  date: string;
  faq: { question: string; answer: string }[];
  contentHtml?: string;
} => {
  if (locale === "ar") {
    const ar = BLOG_POSTS_AR[post.id];
    if (ar) {
      return {
        title: ar.title,
        excerpt: ar.excerpt,
        category: ar.category,
        date: ar.date,
        faq: ar.faq ?? [],
        contentHtml: ar.contentHtml,
      };
    }
  }
  const useEn = locale === "en";
  return {
    title: useEn && post.titleEn ? post.titleEn : post.title,
    excerpt: useEn && post.excerptEn ? post.excerptEn : post.excerpt,
    category: useEn && post.categoryEn ? post.categoryEn : post.category,
    date: useEn && post.dateEn ? post.dateEn : post.date,
    faq: (useEn && post.faqEn && post.faqEn.length > 0 ? post.faqEn : post.faq) ?? [],
    contentHtml: useEn ? post.contentHtmlEn ?? post.contentHtml : post.contentHtml,
  };
};
